// =========================
// SHARED HELPERS
// =========================

function getAtomicContainer() {
  const productList = document.querySelector("atomic-commerce-product-list");
  if (!productList) return null;

  const root = productList.shadowRoot;
  if (!root) return null;

  const listContainer = root.querySelector(".list-root");
  const gridContainer = root.querySelector('[part~="result-list-grid-clickable-container"]');

  return {
    container: listContainer || gridContainer,
    isGridView: !!gridContainer
  };
}

function getAtomicProducts() {
  const data = getAtomicContainer();
  if (!data || !data.container) return [];

  return data.container.querySelectorAll("atomic-product");
}

function getProductShadow(product) {
  return product?.shadowRoot || null;
}

// =========================
// TOOLTIP (PLP PRODUCT BADGE)
// =========================

function processTooltips() {
  const { container, isGridView } = getAtomicContainer() || {};
  if (!container) return;

  const products = container.querySelectorAll("atomic-product");
  if (!products.length) return;

  function getColumns(width) {
    if (width >= 1400) return 4;
    if (width >= 1150) return 3;
    if (width >= 768) return 2;
    return 1;
  }

  function updateColumns() {
    const columns = getColumns(window.innerWidth);
    // use columns here
  }

  /**
   * Recalculate positions for all grid-view price tooltips across all products.
   */
  function refreshAllGridPriceTooltips() {
    products.forEach((prod) => {
      const shadow = getProductShadow(prod);
      if (!shadow) return;
      const cardContainer = shadow.querySelector('.result-component');
      if (!cardContainer) return;
      const cardRect = cardContainer.getBoundingClientRect();
      shadow.querySelectorAll('.plp-tooltip--grid-price').forEach((t) => {
        const w = t._plpWrapper;
        if (!w) return;
        const wrapperRect = w.getBoundingClientRect();
        const iconCenter = wrapperRect.left + wrapperRect.width / 2;
        const cardCenter = cardRect.left + cardRect.width / 2;

        t.style.display = 'block';
        t.style.visibility = 'hidden';
        void t.offsetHeight;

        const tooltipWidth = t.offsetWidth || 255;
        const tooltipLeft = cardCenter - (tooltipWidth / 2);
        const leftFromWrapper = tooltipLeft - wrapperRect.left;
        t.style.left = leftFromWrapper + 'px';

        const caretLeft = iconCenter - tooltipLeft - 8;
        t.style.setProperty('--plp-caret-left', caretLeft + 'px');

        t.style.display = '';
        t.style.visibility = '';
      });
    });
  }

  /**
   * For grid-view price tooltips, calculate the horizontal offset so the
   * tooltip centers over the card while keeping it in its original wrapper
   * (so vertical positioning stays correct).
   */
  function processGridPriceTooltip(wrapper, trigger, tooltip, product) {
    const shadow = getProductShadow(product);
    const cardContainer = shadow?.querySelector('.result-component');
    if (!cardContainer) return;

    if (tooltip.dataset.gridPriceProcessed) return;
    tooltip.dataset.gridPriceProcessed = 'true';

    tooltip.classList.add('plp-tooltip--grid-price');
    tooltip._plpWrapper = wrapper;

    // Since tooltip stays in the wrapper, CSS hover still works,
    // but we also bind JS interactions for keyboard accessibility.
    trigger.addEventListener('mouseenter', function () {
      trigger._plpShow();
    });
    trigger.addEventListener('mouseleave', function () {
      trigger._plpHide();
    });

    // Calculate horizontal positioning
    function updatePosition() {
      const wrapperRect = wrapper.getBoundingClientRect();
      const cardRect = cardContainer.getBoundingClientRect();
      const iconCenter = wrapperRect.left + wrapperRect.width / 2;
      const cardCenter = cardRect.left + cardRect.width / 2;

      // Force tooltip to be measurable (it's hidden by default)
      tooltip.style.display = 'block';
      tooltip.style.visibility = 'hidden';
      void tooltip.offsetHeight;

      const tooltipWidth = tooltip.offsetWidth || 255;
      const tooltipLeft = cardCenter - (tooltipWidth / 2);
      const leftFromWrapper = tooltipLeft - wrapperRect.left;
      tooltip.style.left = leftFromWrapper + 'px';

      // Caret: distance from tooltip left edge to icon center
      const caretLeft = iconCenter - tooltipLeft - 8;
      tooltip.style.setProperty('--plp-caret-left', caretLeft + 'px');

      // Restore hidden state
      tooltip.style.display = '';
      tooltip.style.visibility = '';
    }

    requestAnimationFrame(updatePosition);

    // Recalculate on resize (single global listener)
    if (!window.__plpGridPriceResizeBound) {
      window.__plpGridPriceResizeBound = true;
      window.addEventListener('resize', () => {
        updateColumns();
        refreshAllGridPriceTooltips();
      });
    }
  }

  window.addEventListener('resize', updateColumns);
  updateColumns(); // initial run

  const columns = getColumns(window.innerWidth);

  products.forEach((product, index) => {
    const shadow = getProductShadow(product);
    if (!shadow) return;

    const wrappers = shadow.querySelectorAll(".plp-tooltip-wrapper");

    wrappers.forEach(wrapper => {
      const trigger = wrapper.querySelector(".plp-tooltip-trigger");
      const tooltip = wrapper.querySelector(".plp-tooltip");

      if (!trigger || !tooltip) return;

      tooltip.classList.add("debug-tooltip-found");

      // Handle grid-view price tooltips
      // Identify price tooltips by their .plp-price-tooltip ancestor container
      const isPriceTooltip = wrapper.closest('.plp-price-tooltip');
      if (isPriceTooltip && isGridView) {
        // Bind interactions first so _plpShow/_plpHide exist on trigger
        if (!wrapper.dataset.plpTooltipBound) {
          bindPlpTooltip(wrapper, trigger, tooltip);
        }
        processGridPriceTooltip(wrapper, trigger, tooltip, product);
        return;
      }

      if (isGridView) {
        const isLast = (index + 1) % columns === 0;

        if (isLast) {
          product.classList.add("last-in-row");
          tooltip.classList.add("last-in-row");
        }
      }

      // Attach keyboard interaction handlers (inline script in snippet doesn't run in shadow DOM)
      if (!wrapper.dataset.plpTooltipBound) {
          bindPlpTooltip(wrapper, trigger, tooltip);
      }
    });
  });
}

// =========================
// PLP TOOLTIP KEYBOARD INTERACTION
// =========================

function bindPlpTooltip(wrapper, trigger, tooltip) {
  wrapper.dataset.plpTooltipBound = 'true';

  // Ensure unique ID for aria-describedby association
  if (tooltip) {
    var uniqueId = tooltip.id && tooltip.id !== 'plp-tooltip-'
      ? tooltip.id
      : 'plp-tooltip-' + Math.random().toString(36).substr(2, 9);
    tooltip.id = uniqueId;
    trigger.setAttribute('aria-describedby', uniqueId);
  }

  function show() {
    wrapper.classList.add('plp-tooltip-open');
    trigger.setAttribute('aria-expanded', 'true');
    // For grid-view price tooltips, also add visible class directly to the
    // tooltip because the CSS descendant selector no longer applies
    // also add visible class directly to the tooltip
    if (tooltip.classList.contains('plp-tooltip--grid-price')) {
      tooltip.classList.add('plp-tooltip-visible');
    }
  }
  function hide() {
    wrapper.classList.remove('plp-tooltip-open');
    trigger.setAttribute('aria-expanded', 'false');
    if (tooltip.classList.contains('plp-tooltip--grid-price')) {
      tooltip.classList.remove('plp-tooltip-visible');
    }
  }
  function toggle() {
    if (wrapper.classList.contains('plp-tooltip-open')) hide();
    else show();
  }

  // Expose show/hide on trigger for external callers (e.g., grid price tooltip hover)
  trigger._plpShow = show;
  trigger._plpHide = hide;

  // Click toggles the tooltip
  trigger.addEventListener('click', function (e) {
    e.preventDefault();
    toggle();
  });

  // Show tooltip on focus (keyboard Tab) — stays open until explicitly dismissed
  trigger.addEventListener('focus', function () {
    show();
  });

  // Escape key closes the tooltip
  trigger.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      hide();
      trigger.focus();
    }
  });

  // Close on outside click
  document.addEventListener('click', function (e) {
    if (!wrapper.contains(e.target)) {
      hide();
    }
  });
}

// =========================
// QTY TOOLTIP (ADD TO CART)
// =========================

function handleQtyTooltipVisibility() {
  const products = getAtomicProducts();
  if (!products.length) return;

  let firstTooltip = null;

  products.forEach((product) => {
    const shadow = getProductShadow(product);
    if (!shadow) return;

    const qtyComponent = shadow.querySelector("qty-selector-element");
    if (!qtyComponent) return;

    const tooltip = qtyComponent.querySelector(".plp-qty-tooltip");
    if (!tooltip) return;

    // reset all
    tooltip.classList.remove("plp-show");

    if (!firstTooltip) {
      firstTooltip = tooltip;
    }
  });

  // show only first
  if (!localStorage.getItem("qtyTooltipSeen") && firstTooltip) {
    firstTooltip.classList.add("plp-show");
  }
}

// =========================
// INITIAL LOAD (SEPARATE CONTROL)
// =========================

// 🔹 PLP tooltip (badge)
document.addEventListener("DOMContentLoaded", () => {
  processTooltips();
});

// 🔹 Qty tooltip (controlled separately)
document.addEventListener("DOMContentLoaded", () => {
  setTimeout(() => {
    handleQtyTooltipVisibility();
  }, 2000); // your control stays intact
});